import { isEmpty } from 'lodash';
import { stateStore } from 'store';

/**
 * The single place that decides whether a held quote authority may be used.
 *
 * This module is deliberately plain — no React, no HTTP client. The submission
 * surface (`PaymentMethod/ver2`) cannot be loaded in this project's unit harness
 * because it reaches `src/api`, and the fee-lookup hook is a React hook, so
 * before this seam existed two parts of the app reasoned independently about
 * staleness. That shape is what produced DEV-1385.
 *
 * Each decision below takes the current transaction state and returns a value.
 * None of them dispatches, navigates or makes a request — the callers do that.
 *
 * The spec names four decisions: whether the held quote has been spent, the
 * state that results from invalidating it, whether a new-transfer submission may
 * proceed, and whether a stored quote may be reused for a pricing request whose
 * inputs are unchanged. The first and third resolve to `isQuoteAuthorityUsable`
 * — see its note. The fourth is `isStoredQuoteReusable`, at the bottom of this
 * file; `buildQuoteSignature` beside it is that decision's definition of
 * "unchanged inputs" rather than a fifth decision.
 */

/**
 * The value an invalidated quote field carries.
 *
 * Empty string rather than `null`/`undefined`, matching what the store already
 * holds for a cleared quote, so a caller reading either field sees the same
 * shape it sees today.
 */
export const EMPTY_QUOTE_FIELD = '';

/**
 * Whether the quote authority currently held may be used.
 *
 * Two questions the spec names separately — "has this quote been spent?" and
 * "may a new-transfer submission proceed?" — resolve to this same predicate
 * today, because nothing in the transaction state distinguishes a spent
 * authority from one that was never issued: invalidation empties the field.
 * This module therefore exposes one predicate and does not imply a spent-marker
 * the app does not have. If the backend ever returns a spent-quote signal, that
 * is the point at which the two questions separate.
 */
export const isQuoteAuthorityUsable = (calculatorRoundingResponse) =>
  Boolean(calculatorRoundingResponse?.quoteToken);

/**
 * `isQuoteAuthorityUsable` against current state, read at call time.
 *
 * `stateStore` is reassigned on every render (`src/store/index.js:399`), so the
 * read has to happen inside this call rather than at an untestable call site.
 * A caller that destructured the slice in an earlier render can be re-entered
 * from a callback built several renders ago — the PayTo agreement and one-time
 * code flow does exactly that across a human interaction — and a decision made
 * against that stale snapshot can pass while the payload carries a spent
 * authority.
 */
export const getIsQuoteAuthorityUsable = () =>
  isQuoteAuthorityUsable(stateStore?.calculatorRoundingResponse);

/**
 * The quote authority on current state, read at call time.
 *
 * Not a decision — it is the value read that keeps the refusal decision and the
 * authority placed in the submitted payload on the *same* state. The submission
 * surface destructures `quoteToken` in its render body and builds its payload
 * from that binding, so a submission re-entered from a callback built several
 * renders earlier can pass a fresh decision while the payload still carries the
 * spent authority. That is the PayTo one-time-code variant of the reported
 * incident. Callers place this value rather than the destructured one, and take
 * the usable/not-usable decision from `getIsQuoteAuthorityUsable` beside it.
 */
export const getCurrentQuoteAuthority = () =>
  stateStore?.calculatorRoundingResponse?.quoteToken;

/**
 * The `calculatorRoundingResponse` value that results from invalidating a spent
 * quote. Returns a new value; it does not apply the change in place.
 *
 * `quoteToken` and `__quoteSignature` are emptied **together**. Clearing only
 * the first leaves the dedup guard in `useCalculatorRounding` matching on a
 * later identical amount and returning the cached response, now carrying an
 * empty authority — which the backend rejects exactly as it rejects a spent
 * one, with the diagnostic signal destroyed.
 *
 * Every other field is carried across untouched, `fee`, `originalFee` and
 * `remittance` in particular: discount eligibility derives from the fee data,
 * and a zeroed fee misclassifies flat-fee discounts as inactive. The wholesale
 * `RESET_CALCULATOR_ROUNDING_RESPONSE` the app already has nulls all three, so
 * it is the wrong tool here.
 */
export const invalidateQuoteAuthority = (calculatorRoundingResponse) => ({
  ...calculatorRoundingResponse,
  quoteToken: EMPTY_QUOTE_FIELD,
  __quoteSignature: EMPTY_QUOTE_FIELD,
});

/**
 * `invalidateQuoteAuthority` against current state, read at call time.
 *
 * The store reducer **replaces** the `calculatorRoundingResponse` slice rather
 * than merging it (`src/store/index.js:384-388`), so spreading a snapshot
 * captured in an earlier render would resurrect stale `fee` and `remittance`
 * values over newer ones.
 */
export const buildInvalidatedQuoteAuthorityResponse = () =>
  invalidateQuoteAuthority(stateStore?.calculatorRoundingResponse);

/**
 * The signature of a pricing request: what "the same inputs" means.
 *
 * Moved here from the fee-lookup hook. Behaviourally identical over every input
 * the hook can produce — `remitPayload` is an object literal built at the call
 * site, so the optional chaining added here only makes the function total over a
 * nullish argument the hook cannot pass.
 *
 * It covers every field the quote depends on, so a real change — amount,
 * currency, payout/dmCode, rate, pickup office, country — yields a new signature
 * and still quotes. It is persisted on the response object by the caller so it
 * shares the response lifecycle and is gone when the response is cleared.
 *
 * This is not a fifth decision; it is the definition `isStoredQuoteReusable`
 * compares against, and it lives here so the hook no longer owns half of the
 * rule.
 */
export const buildQuoteSignature = (remitPayload) =>
  [
    remitPayload?.sendingAmount,
    remitPayload?.receivingAmount,
    remitPayload?.rate,
    remitPayload?.currencyCode,
    remitPayload?.dmCode,
    remitPayload?.countryCode,
    remitPayload?.pickupOfficeId,
  ].join('|');

/**
 * Whether a stored quote may be reused for a pricing request whose inputs are
 * unchanged — the fourth decision.
 *
 * Transcribed unchanged from the dedup guard that lived in the fee-lookup hook.
 * Both halves are the rule: the stored signature must equal this request's
 * signature, **and** a stored pricing result must be present. Dropping the
 * second half would reuse a signature-matching response that carries no
 * remittance, and the caller would read amounts off nothing.
 *
 * Steps unmount and remount (Step 2 -> 1 -> 2, or Step 2 -> 3 -> 2) and the
 * remount re-runs the fee effect with the same inputs; without this rule that
 * fires a redundant `/quote` on every return.
 *
 * Deliberately **not** authority-aware. It does not consult `quoteToken`, and it
 * must not start doing so in this change: reuse and spent-ness are separate
 * decisions on this interface, and fusing them here would silently alter when
 * the app re-prices. `isQuoteAuthorityUsable` is the authority question.
 */
export const isStoredQuoteReusable = (
  calculatorRoundingResponse,
  quoteSignature,
) =>
  calculatorRoundingResponse?.__quoteSignature === quoteSignature &&
  !isEmpty(calculatorRoundingResponse?.remittance);
